import * as React from 'react';
import styled from 'styled-components';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import { animated } from 'react-spring';

/**
 * NOTE: This is just `nth-block-label` copied over. The only difference here
 *       is the active animation state `top` property was changed from 1.25rem
 *       to 50%.
 */
interface LabelProps {
  children?: React.ReactNode;
  disabled?: boolean;
  centered?: boolean;
  labelState?: LabelAnimateState;
  htmlFor?: string;
}

export enum LabelAnimateState {
  none = 'NONE',
  inactive = 'INACTIVE',
  active = 'ACTIVE',
}

const transitionLabel = ({
  customLabelStyle,
  labelState,
  centered,
}: {
  customLabelStyle?: React.CSSProperties;
  labelState?: LabelAnimateState;
  centered?: boolean;
}): React.CSSProperties => {
  const baseTransitionStyle: React.CSSProperties = {
    position: 'absolute',
    transition: '200ms ease all',
    cursor: 'text',
    left: centered ? '50%' : '0.5rem',
  };

  return labelState === LabelAnimateState.active
    ? {
        ...baseTransitionStyle,
        top: '0.25rem',
        transform: centered ? 'translateX(-50%)' : 'none',
        color: '#595959',
        ...customLabelStyle,
        fontSize: '0.75rem',
      }
    : {
        ...baseTransitionStyle,
        fontSize: '0.825rem',
        transform: centered ? 'translate(-50%, -50%)' : 'translateY(-50%)',
        top: '50%',
        ...customLabelStyle,
      };
};

const disable = ({
  labelStyleDisabled,
}: {
  labelStyleDisabled: React.CSSProperties;
}): React.CSSProperties => ({
  color: '#595959',
  cursor: 'default',
  ...labelStyleDisabled,
});

const StyledLabel = styled<
  React.FunctionComponent<
    React.ComponentPropsWithRef<'label'> & {
      s?: React.CSSProperties;
    }
  >
>(animated.label)(({ s }) => ({
  position: 'absolute',
  top: '0.5rem',
  left: '0.5rem',
  transform: 'none',
  transition: 'none',
  color: '#595959',
  cursor: 'auto',
  fontSize: '0.875rem',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  ...s,
}));

const Label: React.FunctionComponent<LabelProps> = ({
  children,
  disabled = false,
  centered = false,
  labelState = LabelAnimateState.none,
  htmlFor,
}) => {
  const {
    inputlabelNoneDisabled: labelStyleDisabled,
    inputlabelNoneNone: labelStyle,
  } = useTheme();

  const customLabelStyle = disabled
    ? disable({ labelStyleDisabled })
    : labelStyle;

  return (
    <StyledLabel
      htmlFor={htmlFor}
      s={
        labelState === LabelAnimateState.none
          ? customLabelStyle
          : transitionLabel({ customLabelStyle, labelState, centered })
      }
    >
      {children}
    </StyledLabel>
  );
};

export default withThemeProvider(Label, {
  themeTokens: ['inputlabelNoneDisabled', 'inputlabelNoneNone'],
});
